import { FunctionComponent, useState } from "react";

import { Dialog } from "@bps/fluent-ui";
import { ServerMigrationResponse } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useMigrateServer } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";

import { ServerMigrationResultStage } from "./ServerMigrationResultStage";
import { ServerMigrationReviewStage } from "./ServerMigrationReviewStage";
import { ServerMigrationSelectStage } from "./ServerMigrationSelectStage";

interface ServerMigrationDialogProps {
  tenant: Tenant;
  sourceFieldDeviceId: string;
  onDismiss: () => void;
  onMigrated: () => void;
}

enum Stage {
  SelectDestination = "SelectDestination",
  Review = "Review",
  Complete = "Complete"
}

const titles: Record<Stage, string> = {
  [Stage.SelectDestination]: "Change server",
  [Stage.Review]: "Confirm server migration",
  [Stage.Complete]: "Server migration result"
};

export const ServerMigrationDialog: FunctionComponent<ServerMigrationDialogProps> = ({
  tenant,
  sourceFieldDeviceId,
  onDismiss,
  onMigrated
}) => {
  const [stage, setStage] = useState<Stage>(Stage.SelectDestination);
  const [destinationFieldDeviceId, setDestinationFieldDeviceId] = useState("");
  const [validation, setValidation] = useState<ServerMigrationResponse>();
  const [migration, setMigration] = useState<ServerMigrationResponse>();

  const { mutateAsync, isLoading } = useMigrateServer(sourceFieldDeviceId);

  const validate = async (destinationId: string) => {
    setDestinationFieldDeviceId(destinationId);
    try {
      const response = await mutateAsync({
        destinationFieldDeviceId: destinationId,
        validateOnly: true
      });
      setValidation(response);
      setStage(Stage.Review);
    } catch {
      // Error feedback is handled by the mutation.
    }
  };

  const migrate = async () => {
    try {
      const response = await mutateAsync({
        destinationFieldDeviceId,
        validateOnly: false
      });
      setMigration(response);
      setStage(Stage.Complete);
    } catch {
      // Error feedback is handled by the mutation.
    }
  };

  const close = () => {
    // Once a migration has been attempted the tenant may have changed, so the
    // caller needs to refresh regardless of the outcome.
    if (migration) onMigrated();
    else onDismiss();
  };

  const title =
    stage === Stage.Review && !validation?.success
      ? "Server migration validation failed"
      : titles[stage];

  return (
    <Dialog
      hidden={false}
      onDismiss={isLoading ? undefined : close}
      minWidth={960}
      maxWidth="95%"
      modalProps={{ isBlocking: true }}
      dialogContentProps={{ title, showCloseButton: !isLoading }}
    >
      {stage === Stage.SelectDestination && (
        <ServerMigrationSelectStage
          tenantName={tenant.name}
          sourceFieldDeviceId={sourceFieldDeviceId}
          initialDestinationFieldDeviceId={destinationFieldDeviceId}
          isLoading={isLoading}
          onValidate={validate}
          onCancel={close}
        />
      )}
      {stage === Stage.Review && validation && (
        <ServerMigrationReviewStage
          validation={validation}
          isLoading={isLoading}
          onContinue={migrate}
          onBack={() => setStage(Stage.SelectDestination)}
          onCancel={close}
        />
      )}
      {stage === Stage.Complete && migration && (
        <ServerMigrationResultStage migration={migration} onClose={close} />
      )}
    </Dialog>
  );
};
